/**
 * Dashboard Page - Main overview dashboard
 *
 * Shows:
 * - Key performance metrics (Cash Collected, Revenue, Close Rate)
 * - Call statistics (Show Rate, Calls Due/Taken/Closed)
 * - Average metrics (Cash per Call, Close, Application, Opt-in)
 * - Additional stats (New Cash, MRR, Applications, Opt-ins)
 * - Charts (Closes by Country, Lead Source, Content Performance)
 * - Recent calls table
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LeadTimelineDrawer } from "../components/LeadTimelineDrawer";
import { MetricCard, MetricCardSkeleton, StatCard, StatCardSkeleton, FilterBar } from "../components";
import { AdditionalStatCardSkeleton } from "../components/StatCard";
import { RevenueCalendar } from "../components/RevenueCalendar";
import { branding } from "../config/branding";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useSession } from "../auth/client";
import { useTheme } from "../context/ThemeContext";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ResponsivePie } from "@nivo/pie";
import { DataTable, type ColumnDef } from "../components/ui/data-table";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { EditRowDialog, type FieldDefinition } from "../components/EditRowDialog";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Icons
const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TargetIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

// Chart colors from branding config
const CHART_COLORS = branding.colors.chart;

// Extended colors for pie charts
const PIE_COLORS = [
  "#3B82F6", // blue
  "#EF4444", // red
  "#10B981", // green
  "#F59E0B", // amber
  "#8B5CF6", // purple
  "#EC4899", // pink
  "#06B6D4", // cyan
  "#F97316", // orange
  "#84CC16", // lime
  "#6366F1", // indigo
  "#14B8A6", // teal
];

// Red shades palette for crimson theme
const CRIMSON_CHART_COLORS = [
  "#ef4444", // red-500
  "#dc2626", // red-600
  "#b91c1c", // red-700
  "#991b1b", // red-800
  "#7f1d1d", // red-900
  "#f87171", // red-400
  "#fca5a5", // red-300
  "#c53030", // deep red
  "#e53e3e", // mid red
  "#fc8181", // red-300 lighter
  "#be2626", // alt red-700
];

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatCurrencyAbbreviated = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return formatCurrency(value);
};

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const formatYAxis = (value: number) => {
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toString();
};

interface DashboardMetrics {
  totalCashCollected: number;
  totalRevenue: number;
  closeRate: number;
  showRate: number;
  callsDue: number;
  schedulerCallsDue: number;
  callsTaken: number;
  callsClosed: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
  totalApplicationsCash: number;
  totalOptinsCash: number;
  newCashCollected: number;
  mrrCash: number;
  totalGrossRevenue: number;
  totalApplications: number;
  totalOptins: number;
  totalDMBookings: number;
  totalDMBookingsCash: number;
  cancelledCalls: number;
  cancelledCallsConfirmed: number;
  cancelledCallsUnknown: number;
  cancelledCallsDatePolicy: string;
  cancelRate: number;
}

interface ChartDataItem {
  name: string;
  value: number;
  percentage: number;
  revenue?: number;
}

interface TrendPoint {
  date: string;
  value: number;
}

interface RecentCall {
  id: string;
  _id: string;
  timestamp: string;
  dateCallWasTaken: string;
  leadName: string;
  email: string;
  phone: string;
  closer: string;
  setter: string;
  status: "closed" | "no-show" | "pending" | "not-interested";
  callOutcome: string;
  cashCollected: number;
  notes: string;
  leadSourcePlatform?: string;
  leadSourceContent?: string;
}

// Field definitions for editing calls
const CALL_EDIT_FIELDS: FieldDefinition[] = [
  { key: "leadName", label: "Lead Name", type: "text", required: true },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "closer", label: "Closer", type: "text" },
  { key: "setter", label: "Setter", type: "text" },
  { key: "callOutcome", label: "Call Outcome", type: "text" },
  { key: "notes", label: "Notes", type: "textarea" },
];

interface FilterOption {
  value: string;
  label: string;
}

interface ContentOption extends FilterOption {
  allValues?: string[]; // Multiple video IDs that map to the same title
}

interface FilterOptions {
  closers: string[];
  setters: string[];
  funnels: FilterOption[]; // Now has value/label for normalized display
  contents: ContentOption[];
  countries: string[];
  sources: string[];
}

// Nivo pie chart common theme
const getNivoPieTheme = () => ({
  labels: {
    text: {
      fontSize: 12,
      fontWeight: 400,
    },
  },
});

// Custom tooltip component for Nivo pie charts (works in dark/light mode)
const PieChartTooltip = ({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) => (
  <div className="bg-card text-foreground border border-border rounded-lg shadow-xl px-3 py-2 text-sm">
    <div className="flex items-center gap-2">
      <div
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: datum.color }}
      />
      <span className="font-medium">{datum.id}</span>
    </div>
    <div className="mt-1 text-muted-foreground">
      {datum.value.toLocaleString()} ({datum.data.percentage.toFixed(1)}%)
    </div>
  </div>
);

// Convert chart data to Nivo format
// First deduplicate by name (merge items with same name), then assign colors
const toNivoPieData = (data: ChartDataItem[], colors: string[]) => {
  // Merge items with the same name
  const mergedMap = new Map<string, { value: number; revenue: number }>();
  for (const item of data) {
    const existing = mergedMap.get(item.name);
    if (existing) {
      existing.value += item.value;
      existing.revenue += item.revenue || 0;
    } else {
      mergedMap.set(item.name, { value: item.value, revenue: item.revenue || 0 });
    }
  }

  // Convert back to array and calculate percentages
  const merged = Array.from(mergedMap.entries()).map(([name, data]) => ({
    name,
    value: data.value,
    revenue: data.revenue,
  }));

  // Sort by value descending, then by name alphabetically for stable ordering
  // This prevents items with the same value from jumping around on re-renders
  merged.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    return a.name.localeCompare(b.name);
  });

  // Calculate total for percentages
  const total = merged.reduce((sum, item) => sum + item.value, 0);

  return merged.map((item, index) => ({
    id: item.name, // Name is now unique after merging
    label: item.name,
    value: item.value,
    color: colors[index % colors.length],
    percentage: total > 0 ? (item.value / total) * 100 : 0,
  }));
};

// ─── KPI Bar ─────────────────────────────────────────────────────────────────
type KpiPeriod = "month" | "90days" | "year";

const KPI_PERIOD_LABELS: Record<KpiPeriod, string> = {
  month: "This Month",
  "90days": "90 Days",
  year: "This Year",
};

function KPIBar({ isAdmin, currentCash }: { isAdmin: boolean; currentCash: number }) {
  const [period, setPeriod] = useState<KpiPeriod>("month");
  const [target, setTarget] = useState(0);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchKpi = useCallback(async (p: KpiPeriod) => {
    try {
      const res = await fetch(`/api/kpi?period=${p}`);
      const json = await res.json() as { success: boolean; data?: { target: number } };
      if (json.success && json.data) {
        setTarget(json.data.target);
        
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchKpi(period); }, [period, fetchKpi]);

  const saveTarget = async () => {
    const val = parseFloat(editValue.replace(/[^0-9.]/g, ""));
    if (isNaN(val) || val < 0) return;
    setSaving(true);
    try {
      await fetch("/api/kpi", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ period, target: val }),
      });
      setTarget(val);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const pct = target > 0 ? Math.min((currentCash / target) * 100, 100) : 0;
  const fmt = (n: number) =>
    n >= 1_000_000
      ? `$${(n / 1_000_000).toFixed(2)}M`
      : n >= 1_000
      ? `$${(n / 1_000).toFixed(1)}K`
      : `$${Math.round(n)}`;

  const barGlowColor = pct < 33 ? "#ef4444" : pct < 60 ? "#f97316" : "#22c55e";
  const pctTextColor = pct < 33 ? "#ef4444" : pct < 60 ? "#f97316" : "#22c55e";

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.4 }}
      className="relative w-full rounded-xl border border-border overflow-hidden bg-card"
    >
      {/* Ambient glow strip at top */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, var(--brand-gradient-start), var(--brand-gradient-end), transparent)" }}
      />

      <div className="flex items-center gap-4 px-4 py-3">
        {/* Left: Label + Period */}
        <div className="flex flex-col gap-0.5 flex-shrink-0">
          <span className="text-[10px] uppercase tracking-widest text-white/30 font-semibold">Revenue Goal</span>
          <select
            value={period}
            onChange={(e) => { setPeriod(e.target.value as KpiPeriod); setEditing(false); }}
            className="text-sm font-bold text-white bg-transparent border-none focus:outline-none cursor-pointer appearance-none pr-4"
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2'%3E%3Cpath d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 0 center" }}
          >
            {(Object.keys(KPI_PERIOD_LABELS) as KpiPeriod[]).map((p) => (
              <option key={p} value={p} style={{ background: "#1a1a2e" }}>{KPI_PERIOD_LABELS[p]}</option>
            ))}
          </select>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-white/10 flex-shrink-0" />

        {/* Center: Progress bar + amounts */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          {/* Bar track — clickable to open revenue calendar */}
          <div
            role="button"
            tabIndex={0}
            title="View revenue calendar"
            onClick={() => setCalendarOpen(true)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setCalendarOpen(true); }}
            className="w-full h-2 rounded-full bg-white/10 overflow-hidden cursor-pointer hover:bg-white/15 transition-colors relative group"
          >
            <motion.div
              className="h-full rounded-full relative"
              style={{
                background: "linear-gradient(90deg, #ef4444 0%, #f97316 45%, #eab308 70%, #22c55e 100%)",
                boxShadow: pct > 5 ? `0 0 8px 1px ${barGlowColor}` : "none",
              }}
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
            />
          </div>

          {/* Amount labels */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/50 tabular-nums">
              <span className="text-white font-semibold">{fmt(currentCash)}</span>
              {target > 0 && <span className="text-white/40"> / {fmt(target)}</span>}
            </span>
            {target > 0 && (
              <span
                className="text-xs font-bold tabular-nums"
                style={{ color: target > 0 ? pctTextColor : "rgba(255,255,255,0.4)" }}
              >
                {pct.toFixed(1)}%
              </span>
            )}
            {target === 0 && (
              <span className="text-xs text-white/25 italic">No target set</span>
            )}
          </div>
        </div>

        {/* Right: Admin edit */}
        {isAdmin && (
          <div className="flex-shrink-0">
            {editing ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 bg-white/5 border border-white/15 rounded-lg px-2 py-1">
                  <span className="text-xs text-white/40 font-medium">$</span>
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveTarget(); if (e.key === "Escape") setEditing(false); }}
                    placeholder="500000"
                    autoFocus
                    className="w-24 text-sm bg-transparent text-white placeholder-white/20 focus:outline-none font-medium"
                  />
                </div>
                <button
                  type="button"
                  onClick={saveTarget}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, var(--brand-gradient-start), var(--brand-gradient-end))" }}
                >
                  {saving ? "…" : "Save"}
                </button>
                <button type="button" onClick={() => setEditing(false)} className="text-white/30 hover:text-white/60 transition-colors text-xs px-1">✕</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setEditValue(target > 0 ? String(target) : ""); setEditing(true); }}
                title="Set KPI target"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white/50 hover:text-white/90 bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Set Target
              </button>
            )}
          </div>
        )}
      </div>

      <RevenueCalendar open={calendarOpen} onClose={() => setCalendarOpen(false)} />
    </motion.div>
  );
}

export function Dashboard() {
  const { theme } = useTheme();
  const isCrimson = theme === "crimson";
  const chartColors = CRIMSON_CHART_COLORS;
  const areaStroke = "#991b1b";

  // Get current user session
  const { data: session } = useSession();

  // Use global date range from context (synced with header date picker)
  const { dateRange: globalDateRange, setDateRange, formatForApi, formatForDisplay } = useDateRange();

  // Use shared filters from context (synced with URL via nuqs)
  const {
    dateField,
    closer,
    closers,
    setter,
    setters,
    funnel,
    funnels,
    leadType,
    leadTypes,
    content,
    contents,
    country,
    source,
    setDateField,
    setClosers,
    setSetters,
    setFunnels,
    setLeadTypes,
    setContents,
    setCountry,
    setSource,
    setCountries,
    setSources,
    clearAllFilters,
  } = useFilters();

  // Data states
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [cashTrend, setCashTrend] = useState<TrendPoint[]>([]);
  const [trendType, setTrendType] = useState<"daily" | "monthly">("monthly");
  const [countryData, setCountryData] = useState<ChartDataItem[]>([]);
  const [leadSourceData, setLeadSourceData] = useState<ChartDataItem[]>([]);
  const [contentData, setContentData] = useState<ChartDataItem[]>([]);
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  // Server-side pagination state
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotalItems, setTableTotalItems] = useState(0);
  const [tableTotalPages, setTableTotalPages] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    closers: [],
    setters: [],
    funnels: [],
    contents: [],
    countries: [],
    sources: [],
  });
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [cancelledCallsModal, setCancelledCallsModal] = useState<{ rows: Array<{ callId: string; name: string; email: string; classification: string; cancelledAt: string; scheduledCallAt: string; cancellationReason: string; reasonSource: string; slackThreadUrl: string; closer: string; setter: string; sourcePlatform: string; sourceFunnel: string }>; datePolicy: string; slackAvailable: boolean; slackError?: string; metrics: { total: number; confirmed: number; unknownClassification: number; unknownAttribution: number } } | null>(null);
  const [cancelledCallsLoading, setCancelledCallsLoading] = useState(false);
  const formatEasternDateTime = (value?: string) => value ? new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "Not available";

  const openCancelledCalls = async () => {
    setCancelledCallsLoading(true);
    try {
      const apiDates = formatForApi();
      const params = new URLSearchParams({ startDate: apiDates.startDate, endDate: apiDates.endDate });
      if (closers.length) params.set("closer", closers.join(","));
      if (setters.length) params.set("setter", setters.join(","));
      if (funnels.length) params.set("funnel", funnels.join(","));
      if (contents.length) params.set("content", contents.join(","));
      if (source) params.set("source", source);
      const response = await fetch(`/api/dashboard/cancelled-calls?${params}`);
      const result = await response.json();
      if (!result.success) throw new Error(result.error || "Unable to load cancellation feed.");
      setCancelledCallsModal({ rows: result.leads || [], datePolicy: result.datePolicy || "Cancellation time (America/New_York)", slackAvailable: Boolean(result.slackDetailsAvailable), slackError: result.slackDetailsError, metrics: result.metrics || { total: 0, confirmed: 0, unknownClassification: 0, unknownAttribution: 0 } });
    } catch (error) {
      console.error("Failed to fetch cancelled calls:", error);
      setCancelledCallsModal({ rows: [], datePolicy: "Cancellation time (America/New_York)", slackAvailable: false, slackError: error instanceof Error ? error.message : "Unable to load cancellation feed.", metrics: { total: 0, confirmed: 0, unknownClassification: 0, unknownAttribution: 0 } });
    } finally {
      setCancelledCallsLoading(false);
    }
  };

  // Call Bookings funnel cohort.
  const [bookingChannel, setBookingChannel] = useState<"" | "inbound" | "outbound">("");
  const [funnelType, setFunnelType] = useState<"" | "webinar" | "bookCall" | "application" | "leadMagnet">("");

  // Chart filter mode: "closed" shows only closed leads, "all" shows all leads
  const [chartFilterMode, setChartFilterMode] = useState<"closed" | "all">("closed");
  const [exportingDashboard, setExportingDashboard] = useState<"csv" | "xlsx" | "pdf" | null>(null);

  // Edit/Delete state for recent calls
  const [editingCall, setEditingCall] = useState<RecentCall | null>(null);
  const [deletingCall, setDeletingCall] = useState<RecentCall | null>(null);

  // Lead timeline drawer
  const [selectedLeadEmail, setSelectedLeadEmail] = useState<string | null>(null);

  // Status badge helper
  const getStatusBadgeClass = (status: RecentCall["status"]) => {
    const styles: Record<string, string> = {
      closed: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
      "no-show": "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
      pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
      "not-interested": "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    };
    return styles[status] || styles.pending;
  };

  const getStatusLabel = (status: RecentCall["status"]) => {
    const labels: Record<string, string> = {
      closed: "Closed",
      "no-show": "No Show",
      pending: "Pending",
      "not-interested": "Not Interested",
    };
    return labels[status] || status;
  };

  // Define columns for recent calls table
  const recentCallsColumns: ColumnDef<RecentCall>[] = useMemo(() => [
    {
      id: "timestamp",
      header: "Timestamp",
      cell: (row) => {
        if (!row.timestamp) return <span className="text-muted-foreground">-</span>;
        const date = new Date(row.timestamp);
        return (
          <span className="text-muted-foreground text-xs">
            {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        );
      },
    },
    {
      id: "dateCallWasTaken",
      header: "Date Call Was Taken",
      cell: (row) => (
        row.dateCallWasTaken ? (
          <span className="text-muted-foreground">{row.dateCallWasTaken}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      ),
    },
    {
      id: "leadName",
      header: "Lead Name",
      accessorKey: "leadName",
      className: "font-medium",
    },
    {
      id: "closer",
      header: "Closer",
      accessorKey: "closer",
    },
    {
      id: "setter",
      header: "Setter",
      accessorKey: "setter",
    },
    {
      id: "source",
      header: "Source",
      cell: (row) => {
        const platform = row.leadSourcePlatform || "";
        const content = row.leadSourceContent || "";
        if (!platform && !content) return <span className="text-muted-foreground">-</span>;
        return (
          <span className="text-xs text-muted-foreground">
            {platform}{platform && content ? " | " : ""}{content}
          </span>
        );
      },
    },
    {
      id: "status",
      header: "Call Outcome",
      cell: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(row.status)}`}>
          {row.callOutcome || "-"}
        </span>
      ),
    },
    {
      id: "cashCollected",
      header: "Cash Collected",
      cell: (row) => (
        row.cashCollected > 0 ? (
          <span className="text-green-600 font-medium">{formatCurrency(row.cashCollected)}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      ),
      headerClassName: "text-right",
      className: "text-right",
    },
  ], []);

  // Handle edit
  const handleEditCall = (call: RecentCall) => {
    setEditingCall(call);
  };

  // Handle save
  const handleSaveCall = async (data: Record<string, unknown>) => {
    // Map frontend field names to backend field names
    const mappedData = {
      prospectName: data.leadName,
      prospectEmail: data.email,
      prospectNumber: data.phone,
      closerName: data.closer,
      setterName: data.setter,
      callOutcome: data.callOutcome,
      callNotes: data.notes,
    };

    const response = await fetch(`/api/leads/${data._id || data.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(mappedData),
    });

    if (!response.ok) {
      throw new Error("Failed to update call");
    }

    // Refresh data
    fetchData(false);
  };

  // Handle delete
  const handleDeleteCall = (call: RecentCall) => {
    setDeletingCall(call);
  };

  // Confirm delete
  const confirmDeleteCall = async () => {
    if (!deletingCall) return;

    const response = await fetch(`/api/leads/${deletingCall._id || deletingCall.id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Failed to delete call");
    }

    // Refresh data
    fetchData(false);
  };

  // Fetch data from API
  // tableOnly: if true, only show table loading (for pagination changes)
  const fetchData = useCallback(async (showLoading = true, tableOnly = false) => {
    if (showLoading) {
      if (tableOnly) {
        setTableLoading(true);
      } else {
        setIsLoading(true);
      }
    }
    try {
      // Use global date range from context
      const apiDates = formatForApi();
      const params = new URLSearchParams();
      params.set("startDate", apiDates.startDate);
      params.set("endDate", apiDates.endDate);
      if (dateField) params.set("dateField", dateField);
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (leadTypes.length > 0) params.set("leadType", leadTypes.join(","));
      if (contents.length > 0) params.set("content", contents.join(","));
      if (country) params.set("country", country);
      if (source) params.set("source", source);
      if (bookingChannel) params.set("bookingChannel", bookingChannel);
      if (funnelType) params.set("funnelType", funnelType);
      // Chart filter: closedOnly=true shows only closed leads in charts
      params.set("closedOnly", chartFilterMode === "closed" ? "true" : "false");
      // Pagination params
      params.set("page", String(tablePage));
      params.set("pageSize", String(tablePageSize));

      const response = await fetch(`/api/dashboard/summary?${params}`);
      const result = await response.json();

      if (!result.success) throw new Error(result.error);

      const { data } = result;

      setMetrics({
        totalCashCollected: data.metrics.totalCashCollected || 0,
        totalRevenue: data.metrics.totalRevenue || 0,
        closeRate: data.metrics.closeRate || 0,
        showRate: data.metrics.showRate || 0,
        callsDue: data.metrics.callsDue || 0,
        schedulerCallsDue: data.metrics.schedulerCallsDue || 0,
        callsTaken: data.metrics.callsTaken || 0,
        callsClosed: data.metrics.callsClosed || 0,
        avgCashPerCall: data.metrics.avgCashPerCall || 0,
        avgCashPerClose: data.metrics.avgCashPerClose || 0,
        avgCashPerApplication: data.metrics.avgCashPerApplication || 0,
        avgCashPerOptin: data.metrics.avgCashPerOptin || 0,
        totalApplicationsCash: data.metrics.totalApplicationsCash || 0,
        totalOptinsCash: data.metrics.totalOptinsCash || 0,
        newCashCollected: data.metrics.newCashCollected || 0,
        mrrCash: data.metrics.mrrCash || 0,
        totalGrossRevenue: data.metrics.totalGrossRevenue || 0,
        totalApplications: data.metrics.totalApplications || 0,
        totalOptins: data.metrics.totalOptins || 0,
        totalDMBookings: data.metrics.totalDMBookings || 0,
        totalDMBookingsCash: data.metrics.totalDMBookingsCash || 0,
        cancelledCalls: data.metrics.cancelledCalls || 0,
        cancelledCallsConfirmed: data.metrics.cancelledCallsConfirmed || 0,
        cancelledCallsUnknown: data.metrics.cancelledCallsUnknown || 0,
        cancelledCallsDatePolicy: data.metrics.cancelledCallsDatePolicy || "Cancellation time (America/New_York)",
        cancelRate: data.metrics.cancelRate || 0,
      });

      if (data.filterOptions) {
        setFilterOptions({
          closers: data.filterOptions.closers || [],
          setters: data.filterOptions.setters || [],
          funnels: data.filterOptions.funnels || [],
          contents: data.filterOptions.contents || [],
          countries: data.filterOptions.countries || [],
          sources: data.filterOptions.sources || [],
        });
      }

      setCashTrend(data.cashTrend || []);
      setTrendType(data.trendType || "monthly");
      setCountryData(data.countryData || []);
      setLeadSourceData(data.leadSourceData || []);
      setContentData(data.contentData || []);
      setRecentCalls(data.recentCalls || []);

      // Update pagination state from server response
      if (data.pagination) {
        setTableTotalItems(data.pagination.totalItems);
        setTableTotalPages(data.pagination.totalPages);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch dashboard metrics:", error);
    } finally {
      if (showLoading) {
        if (tableOnly) {
          setTableLoading(false);
        } else {
          setIsLoading(false);
        }
      }
    }
  }, [formatForApi, dateField, closers, setters, funnels, leadTypes, contents, country, source, bookingChannel, funnelType, chartFilterMode, tablePage, tablePageSize]);

  // Dashboard export (Joey, Oct 27 2026): CSV / Excel / PDF of exactly what the dashboard shows
  // under the active filters — metric cards, cash trend, the three breakdown charts and every call row.
  const downloadDashboardExport = async (format: "csv" | "xlsx" | "pdf") => {
    setExportingDashboard(format);
    try {
      const apiDates = formatForApi();
      const params = new URLSearchParams();
      params.set("startDate", apiDates.startDate);
      params.set("endDate", apiDates.endDate);
      if (dateField) params.set("dateField", dateField);
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (leadTypes.length > 0) params.set("leadType", leadTypes.join(","));
      if (contents.length > 0) params.set("content", contents.join(","));
      if (country) params.set("country", country);
      if (source) params.set("source", source);
      if (bookingChannel) params.set("bookingChannel", bookingChannel);
      if (funnelType) params.set("funnelType", funnelType);
      params.set("closedOnly", chartFilterMode === "closed" ? "true" : "false");
      params.set("page", "1");
      params.set("pageSize", "20000");
      const response = await fetch(`/api/dashboard/summary?${params}`);
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || "Unable to prepare dashboard export");
      const data = result.data as { metrics: Partial<DashboardMetrics>; cashTrend?: TrendPoint[]; countryData?: ChartDataItem[]; leadSourceData?: ChartDataItem[]; contentData?: ChartDataItem[]; recentCalls?: RecentCall[] };
      const m = data.metrics || {};
      const num = (v: number | undefined) => Number(v || 0);
      const money = (v: number | undefined) => formatCurrency(num(v));
      const count = (v: number | undefined) => num(v).toLocaleString("en-US");
      const pct = (v: number | undefined) => formatPercent(num(v));
      const funnelLabel = (value: string) => filterOptions.funnels.find((f) => f.value === value)?.label || value;
      const contentLabel = (value: string) => filterOptions.contents.find((c) => c.value === value)?.label || value;
      const filterRows: string[][] = [
        ["Date range", formatForDisplay()],
        ["Date field", dateField === "timestamp" ? "Timestamp" : "Date Call Was Taken"],
        ["Call Booking Type", bookingChannel === "inbound" ? "Inbound (Self-Booked)" : bookingChannel === "outbound" ? "Outbound (Setter Booked)" : "All"],
        ["Funnels", funnels.length ? funnels.map(funnelLabel).join("; ") : "All Funnels"],
        ["Funnel Type", funnelType === "webinar" ? "Webinar" : funnelType === "bookCall" ? "Book a Call" : funnelType === "application" ? "Application" : funnelType === "leadMagnet" ? "Lead Magnet" : "All"],
        ["Lead Types", leadTypes.length ? leadTypes.map((t) => (t === "applicant" ? "Applicant" : t === "optin" ? "Opt-in" : t)).join("; ") : "All Lead Types"],
        ["Content", contents.length ? contents.map(contentLabel).join("; ") : "All"],
        ["Closers", closers.length ? closers.join("; ") : "All"],
        ["Setters", setters.length ? setters.join("; ") : "All"],
        ["Country", country || "All"],
        ["Source", source || "All"],
        ["Charts show", chartFilterMode === "closed" ? "Closed leads only" : "All leads"],
        ["Exported", new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", dateStyle: "medium", timeStyle: "short" }).format(new Date()) + " ET"],
      ];
      const metricRows: string[][] = [
        ["Main", "Cash Collected", money(m.totalCashCollected)],
        ["Main", "Revenue", money(m.totalRevenue)],
        ["Main", "Close Rate", pct(m.closeRate)],
        ["Call Statistics", "Show Rate", pct(m.showRate)],
        ["Call Statistics", "Calls Due (Scheduler)", count(m.schedulerCallsDue)],
        ["Call Statistics", "Calls Cancelled", count(m.cancelledCalls)],
        ["Call Statistics", "Calls Due (EOCs)", count(m.callsDue)],
        ["Call Statistics", "Calls Taken", count(m.callsTaken)],
        ["Call Statistics", "Calls Closed", count(m.callsClosed)],
        ["Call Statistics", "Cancel Rate", pct(m.cancelRate)],
        ["Average Metrics", "Avg. Cash/Call", money(m.avgCashPerCall)],
        ["Average Metrics", "Avg. Cash/Close", money(m.avgCashPerClose)],
        ["Average Metrics", "Avg. Cash/Application", money(m.avgCashPerApplication)],
        ["Average Metrics", "Avg. Cash/Opt-in", money(m.avgCashPerOptin)],
        ["Cash Totals", "Total Cash (Applications)", money(m.totalApplicationsCash)],
        ["Cash Totals", "Total Cash (Opt-ins)", money(m.totalOptinsCash)],
        ["Cash Totals", "Total Cash (DM Bookings)", money(m.totalDMBookingsCash)],
        ["Additional Statistics", "New Cash Collected", money(m.newCashCollected)],
        ["Additional Statistics", "MRR", money(m.mrrCash)],
        ["Additional Statistics", "Total Gross Revenue", money(m.totalGrossRevenue)],
        ["Additional Statistics", "Total Applications", count(m.totalApplications)],
        ["Additional Statistics", "Total Opt-ins", count(m.totalOptins)],
        ["Additional Statistics", "Total DM Bookings", count(m.totalDMBookings)],
      ];
      const chartWord = chartFilterMode === "closed" ? "Closes" : "Leads";
      const chartSections: Array<{ title: string; rows: string[][] }> = [
        { title: `${chartWord} by Country`, rows: (data.countryData || []).map((d) => [d.name, count(d.value), `${num(d.percentage).toFixed(1)}%`, d.revenue !== undefined ? money(d.revenue) : ""]) },
        { title: `${chartWord} by Lead Source`, rows: (data.leadSourceData || []).map((d) => [d.name, count(d.value), `${num(d.percentage).toFixed(1)}%`, d.revenue !== undefined ? money(d.revenue) : ""]) },
        { title: `${chartWord} by Content`, rows: (data.contentData || []).map((d) => [d.name, count(d.value), `${num(d.percentage).toFixed(1)}%`, d.revenue !== undefined ? money(d.revenue) : ""]) },
      ];
      const chartHeader = ["Name", "Count", "Share", "Cash"];
      const trendRows: string[][] = (data.cashTrend || []).map((t) => [t.date, money(t.value)]);
      const callHeader = ["Timestamp (ET)", "Date Call Was Taken", "Lead Name", "Email", "Phone", "Closer", "Setter", "Status", "Call Outcome", "Cash Collected", "Lead Source", "Content"];
      const statusText: Record<string, string> = { closed: "Closed", "no-show": "No Show", pending: "Pending", "not-interested": "Not Interested" };
      const callRows: string[][] = (data.recentCalls || []).map((c) => [
        c.timestamp ? new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", dateStyle: "short", timeStyle: "short" }).format(new Date(c.timestamp)) : "",
        c.dateCallWasTaken || "", c.leadName || "", c.email || "", c.phone || "", c.closer || "", c.setter || "",
        statusText[c.status] || c.status || "", c.callOutcome || "", money(c.cashCollected), c.leadSourcePlatform || "", c.leadSourceContent || "",
      ]);
      // Cash by Funnel Type (Joey, Oct 27 2026, for Jeremy Haynes AI benchmarks): the same summary call once per
      // funnel type plus once with no Funnel Type, all other active filters kept, so each row matches the dashboard
      // filtered to that type. "No funnel type" = All minus the four types (DM / setter-only leads).
      const ftKeys = [
        { key: "application", label: "Application" },
        { key: "webinar", label: "Webinar" },
        { key: "leadMagnet", label: "Lead Magnet" },
        { key: "bookCall", label: "Book a Call" },
      ] as const;
      const ftFetch = async (ft: string) => {
        const p = new URLSearchParams(params);
        p.delete("funnelType");
        if (ft) p.set("funnelType", ft);
        p.set("page", "1");
        p.set("pageSize", "1");
        const r = await fetch(`/api/dashboard/summary?${p}`);
        const j = await r.json();
        if (!r.ok || !j.success) throw new Error(j.error || "Unable to load cash by funnel type");
        return (j.data?.metrics || {}) as Partial<DashboardMetrics>;
      };
      const [ftAll, ...ftEach] = await Promise.all([ftFetch(""), ...ftKeys.map((f) => ftFetch(f.key))]);
      type FtRow = { label: string; cash: number; newCash: number; calls: number; closes: number };
      const ftRowsRaw: FtRow[] = ftKeys.map((f, i) => ({ label: f.label, cash: num(ftEach[i].totalCashCollected), newCash: num(ftEach[i].newCashCollected), calls: num(ftEach[i].callsTaken), closes: num(ftEach[i].callsClosed) }));
      const ftSum = (k: keyof Omit<FtRow, "label">) => ftRowsRaw.reduce((a, r) => a + r[k], 0);
      ftRowsRaw.push({ label: "No funnel type (DM / setter-only)", cash: num(ftAll.totalCashCollected) - ftSum("cash"), newCash: num(ftAll.newCashCollected) - ftSum("newCash"), calls: num(ftAll.callsTaken) - ftSum("calls"), closes: num(ftAll.callsClosed) - ftSum("closes") });
      const ftTotal: FtRow = { label: "Total", cash: num(ftAll.totalCashCollected), newCash: num(ftAll.newCashCollected), calls: num(ftAll.callsTaken), closes: num(ftAll.callsClosed) };
      const ftLine = (r: FtRow) => [
        r.label,
        money(r.cash),
        ftTotal.cash > 0 ? `${((r.cash / ftTotal.cash) * 100).toFixed(1)}%` : "0.0%",
        count(r.calls),
        count(r.closes),
        r.calls > 0 ? `${((r.closes / r.calls) * 100).toFixed(1)}%` : "0.0%",
        r.closes > 0 ? money(r.newCash / r.closes) : money(0),
      ];
      const ftHeader = ["Funnel Type", "Cash Collected", "% of Total Cash", "Calls Taken", "Closes", "Close Rate", "Avg. Cash/Close"];
      const ftRows: string[][] = ftRowsRaw.map(ftLine);
      const ftTotalRow = ftLine(ftTotal);
      const ftScope = `Date range: ${formatForDisplay()} (Eastern time, by ${dateField === "timestamp" ? "Timestamp" : "Date Call Was Taken"}). Other active filters applied; the Funnel Type filter is ignored here so every type shows.`;
      const ftDefs: string[][] = [
        ["Attribution", "Each call is credited to the lead's most recent self-initiated funnel touch before that call (last touch). Setter-booked iClosed calls never count as a touch."],
        ["Application", "Last touch was a Typeform application (any Typeform form, incl. the Lead Scoring opt-in)."],
        ["Webinar", "Last touch was a webinar opt-in (Evergreen / Cold webinar, Creators Canvas, Free Training); an application within 30 days of a webinar opt-in, or the Evergreen Webinar application, stays Webinar."],
        ["Lead Magnet", "Last touch was a lead-magnet opt-in (18Q Framework, Q4 Checklist, 30 Days of Content, Product Release Handbook, other resource opt-ins)."],
        ["Book a Call", "Last touch was an application or self-booking on an inbound iClosed link (Evergreen, Evergreen webinar, Cold webinar book-a-call, Book-a-call ads, Referral)."],
        ["No funnel type", "DM / setter-only leads with no opt-in or application (e.g. CWM DM iClosed, DM Setter Booked). Equals All minus the four types."],
        ["Metrics", "Cash Collected = new cash + MRR (dashboard Cash Collected). Calls Taken and Closes from EOC forms. Close Rate = Closes / Calls Taken. Avg. Cash/Close = new cash / Closes (dashboard definition)."],
      ];
      const activeFilterText = filterRows.filter(([k, v]) => !["Date range", "Date field", "Exported", "Funnel Type", "Charts show"].includes(k) && !/^All/.test(v)).map(([k, v]) => `${k}: ${v}`).join(" · ") || "none";
      const filename = `urban-unity-dashboard-${apiDates.startDate}-to-${apiDates.endDate}`;
      const saveBlob = (blob: Blob, name: string) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = name;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(link.href), 2000);
      };
      if (format === "csv") {
        const lines: string[][] = [
          ["Urban Unity Dashboard Export"], [],
          ["Filters"], ...filterRows, [],
          ["Cash by Funnel Type"], [ftScope], ftHeader, ...ftRows, ftTotalRow, [], ["Definitions"], ...ftDefs, [],
          ["Section", "Metric", "Value"], ...metricRows, [],
          ...chartSections.flatMap((s) => [[s.title], chartHeader, ...s.rows, []]),
          ["Cash Trend"], ["Period", "Cash Collected"], ...trendRows, [],
          [`Calls (${callRows.length})`], callHeader, ...callRows,
        ];
        const csv = lines.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
        saveBlob(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }), `${filename}.csv`);
      } else if (format === "xlsx") {
        const wb = XLSX.utils.book_new();
        const summary = XLSX.utils.aoa_to_sheet([["Urban Unity Dashboard Export"], [], ["Filter", "Value"], ...filterRows, [], ["Section", "Metric", "Value"], ...metricRows]);
        summary["!cols"] = [{ wch: 24 }, { wch: 30 }, { wch: 22 }];
        XLSX.utils.book_append_sheet(wb, summary, "Dashboard Metrics");
        const ftSheet = XLSX.utils.aoa_to_sheet([["Cash by Funnel Type"], [ftScope], [`Active filters: ${activeFilterText}`], [], ftHeader, ...ftRows, ftTotalRow, [], ["Definitions"], ...ftDefs]);
        ftSheet["!cols"] = [{ wch: 34 }, { wch: 16 }, { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ftSheet, "Cash by Funnel Type");
        const charts = XLSX.utils.aoa_to_sheet(chartSections.flatMap((s) => [[s.title], chartHeader, ...s.rows, []]));
        charts["!cols"] = [{ wch: 40 }, { wch: 10 }, { wch: 10 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, charts, "Breakdowns");
        const trend = XLSX.utils.aoa_to_sheet([["Period", "Cash Collected"], ...trendRows]);
        trend["!cols"] = [{ wch: 14 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, trend, "Cash Trend");
        const calls = XLSX.utils.aoa_to_sheet([callHeader, ...callRows]);
        calls["!cols"] = [{ wch: 18 }, { wch: 14 }, { wch: 24 }, { wch: 30 }, { wch: 16 }, { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 22 }, { wch: 14 }, { wch: 18 }, { wch: 28 }];
        XLSX.utils.book_append_sheet(wb, calls, "Calls");
        XLSX.writeFile(wb, `${filename}.xlsx`);
      } else {
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
        const head = { fillColor: [80, 50, 21] as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontStyle: "bold" as const };
        doc.setFontSize(16);
        doc.text("Urban Unity Dashboard Export", 40, 40);
        doc.setFontSize(9);
        doc.setTextColor(90);
        doc.text(`${formatForDisplay()} · Eastern time`, 40, 56);
        doc.setTextColor(0);
        const lastY = () => (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 70;
        // Page 1: Cash by Funnel Type (table, bar chart, definitions)
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Cash by Funnel Type", 40, 80);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(90);
        doc.text(doc.splitTextToSize(ftScope, 712), 40, 93);
        doc.text(doc.splitTextToSize(`Active filters: ${activeFilterText}`, 712), 40, 104);
        doc.setTextColor(0);
        autoTable(doc, {
          startY: 112,
          head: [ftHeader],
          body: ftRows,
          foot: [ftTotalRow],
          styles: { fontSize: 8.5 },
          headStyles: head,
          footStyles: { fillColor: [242, 204, 147], textColor: [10, 6, 2], fontStyle: "bold" },
          columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
          margin: { left: 40, right: 40 },
        });
        {
          // Horizontal bar chart: cash collected per funnel type
          let y = lastY() + 22;
          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");
          doc.text("Cash collected by funnel type", 40, y);
          doc.setFont("helvetica", "normal");
          y += 10;
          const maxCash = Math.max(1, ...ftRowsRaw.map((r) => r.cash));
          const labelW = 170, barMax = 400, barH = 13;
          for (const r of ftRowsRaw) {
            const w = Math.max(0, (r.cash / maxCash) * barMax);
            doc.setFontSize(8);
            doc.setTextColor(40);
            doc.text(r.label, 40, y + 9.5);
            doc.setFillColor(201, 152, 96);
            if (w > 0) doc.rect(40 + labelW, y, w, barH, "F");
            doc.setTextColor(0);
            doc.text(`${money(r.cash)}  (${ftTotal.cash > 0 ? ((r.cash / ftTotal.cash) * 100).toFixed(1) : "0.0"}%)`, 40 + labelW + w + 6, y + 9.5);
            y += barH + 5;
          }
          autoTable(doc, { startY: y + 10, head: [["Term", "Definition"]], body: ftDefs, styles: { fontSize: 7.5, cellPadding: 3, overflow: "linebreak" }, headStyles: head, columnStyles: { 0: { cellWidth: 90, fontStyle: "bold" } }, margin: { left: 40, right: 40 } });
        }
        doc.addPage();
        doc.setFontSize(12);
        doc.text("Filters & dashboard metrics", 40, 40);
        autoTable(doc, { startY: 52, head: [["Filter", "Value"]], body: filterRows, styles: { fontSize: 8 }, headStyles: head, margin: { left: 40, right: 40 }, tableWidth: 360 });
        autoTable(doc, { startY: lastY() + 16, head: [["Section", "Metric", "Value"]], body: metricRows, styles: { fontSize: 8 }, headStyles: head, margin: { left: 40, right: 40 }, tableWidth: 460 });
        for (const s of chartSections) {
          autoTable(doc, { startY: lastY() + 16, head: [[s.title, "Count", "Share", "Cash"]], body: s.rows.length ? s.rows : [["No data", "", "", ""]], styles: { fontSize: 8 }, headStyles: head, margin: { left: 40, right: 40 }, tableWidth: 460 });
        }
        autoTable(doc, { startY: lastY() + 16, head: [["Cash Trend", "Cash Collected"]], body: trendRows.length ? trendRows : [["No data", ""]], styles: { fontSize: 8 }, headStyles: head, margin: { left: 40, right: 40 }, tableWidth: 260 });
        doc.addPage();
        doc.setFontSize(12);
        doc.text(`Calls (${callRows.length})`, 40, 40);
        autoTable(doc, { startY: 52, head: [callHeader], body: callRows.length ? callRows : [["No calls", "", "", "", "", "", "", "", "", "", "", ""]], styles: { fontSize: 6.5, cellPadding: 2, overflow: "linebreak" }, headStyles: head, margin: { left: 24, right: 24 } });
        doc.save(`${filename}.pdf`);
      }
    } catch (error) {
      console.error("Failed to export dashboard:", error);
      window.alert(error instanceof Error ? error.message : "Unable to prepare dashboard export");
    } finally {
      setExportingDashboard(null);
    }
  };

  // Auto-remove invalid filters when available options change
  // (e.g., when selecting a funnel that has no data for certain closers)
  useEffect(() => {
    // Skip if filterOptions haven't loaded yet
    if (!filterOptions.closers.length && !filterOptions.setters.length && !filterOptions.funnels.length) {
      return;
    }

    const filtersToRemove: Record<string, string[]> = {};

    // Check closer filter - if selected closers not in available options, remove them
    if (closers.length > 0 && filterOptions.closers.length > 0) {
      if (!closers.every(c => filterOptions.closers.includes(c))) {
        filtersToRemove.closers = [];
      }
    }

    // Check setter filter
    if (setters.length > 0 && filterOptions.setters.length > 0) {
      if (!setters.every(s => filterOptions.setters.includes(s))) {
        filtersToRemove.setters = [];
      }
    }

    // Check funnel filter (uses value/label format)
    if (funnels.length > 0 && filterOptions.funnels.length > 0) {
      const funnelValues = filterOptions.funnels.map(f => f.value);
      if (!funnels.every(f => funnelValues.includes(f))) {
        filtersToRemove.funnels = [];
      }
    }

    // Check content filter (uses value/label format)
    if (contents.length > 0 && filterOptions.contents.length > 0) {
      const contentValues = filterOptions.contents.map(c => c.value);
      if (!contents.every(c => contentValues.includes(c))) {
        filtersToRemove.contents = [];
      }
    }

    // Check country filter
    if (country && filterOptions.countries.length > 0) {
      if (!filterOptions.countries.includes(country)) {
        filtersToRemove.countries = [];
      }
    }

    // Check source filter
    if (source && filterOptions.sources.length > 0) {
      if (!filterOptions.sources.includes(source)) {
        filtersToRemove.sources = [];
      }
    }

    // Remove invalid filters
    if (Object.keys(filtersToRemove).length > 0) {
      if ('closers' in filtersToRemove) setClosers([]);
      if ('setters' in filtersToRemove) setSetters([]);
      if ('funnels' in filtersToRemove) setFunnels([]);
      if ('contents' in filtersToRemove) setContents([]);
      if ('countries' in filtersToRemove) setCountries([]);
      if ('sources' in filtersToRemove) setSources([]);
    }
  }, [filterOptions, closers, setters, funnels, contents, country, source, setClosers, setSetters, setFunnels, setContents, setCountries, setSources]);

  // Track if the next fetch should be table-only (for pagination changes)
  const isPaginationChangeRef = useRef(false);

  // Reset to page 1 when filters change
  useEffect(() => {
    isPaginationChangeRef.current = false;
    setTablePage(1);
  }, [globalDateRange, dateField, closers, setters, funnels, leadTypes, contents, country, source, funnelType, chartFilterMode]);

  // Handle pagination changes - mark as pagination-only before state update
  const handlePageChange = (page: number) => {
    isPaginationChangeRef.current = true;
    setTablePage(page);
  };

  const handlePageSizeChange = (size: number) => {
    isPaginationChangeRef.current = true;
    setTablePageSize(size);
    setTablePage(1);
  };

  // Fetch data when dependencies change
  useEffect(() => {
    const isTableOnly = isPaginationChangeRef.current;
    isPaginationChangeRef.current = false; // Reset for next change
    fetchData(true, isTableOnly);
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchData(false), 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Clear all filters (date range is managed globally by the header picker)
  const handleClearAll = () => {
    clearAllFilters();
    setBookingChannel("");
    setFunnelType("");
  };

  // Chart click handlers
  const handleCountryClick = (name: string) => {
    if (name === "Other") return;
    setCountry(country === name ? "" : name);
  };

  const handleSourceClick = (name: string) => {
    if (name === "Other" || name === "N/A") return;
    setSource(source === name ? "" : name);
  };

  const handleContentClick = (name: string) => {
    if (name === "Other") return;
    const matchingOption = filterOptions.contents.find(
      (c) => c.value === name || c.label === name || (c.value && name.startsWith(c.label))
    );
    const valueToSet = matchingOption?.value || name;
    if (contents.includes(valueToSet)) {
      setContents(contents.filter(c => c !== valueToSet));
    } else {
      setContents([...contents, valueToSet]);
    }
  };

  // Only show skeleton on initial load, not when filtering (keep old data visible)
  if (isLoading && !metrics) {
    return (
      <div className="space-y-6">
        {/* Metric Cards Skeleton */}
        <div className="grid gap-6 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <MetricCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Call Statistics Skeleton */}
        <div className="grid gap-4 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Average Metrics Skeleton */}
        <div className="grid gap-4 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i + 4} />
          ))}
        </div>

        {/* Cash Totals Skeleton */}
        <div className="grid gap-4 grid-cols-2">
          {[0, 1].map((i) => (
            <StatCardSkeleton key={i} index={i + 8} />
          ))}
        </div>

        {/* Additional Statistics Skeleton */}
        <div className="grid gap-4 md:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <AdditionalStatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Chart Skeletons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="relative overflow-hidden bg-card rounded-xl border border-border p-6"
        >
          <div className="h-5 w-40 rounded bg-muted animate-pulse mb-4" />
          <div className="h-[400px] rounded-lg bg-muted/30 animate-pulse flex items-center justify-center">
            <motion.div
              className="w-32 h-32 rounded-full border-4 border-muted border-t-muted-foreground/30"
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <motion.div
            className="absolute inset-0"
            animate={{ x: ["-100%", "100%"] }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            style={{
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)",
            }}
          />
        </motion.div>
      </div>
    );
  }

  // Get greeting based on time of day with user's first name
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  };


  return (
    <div className="space-y-4">
      {/* Row 1: Greeting + Controls */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex items-center justify-between gap-2"
      >
        <motion.h1
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="text-lg md:text-2xl font-bold tracking-tight flex-shrink-0"
        >
          {getGreeting()}
        </motion.h1>

        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1" aria-label="Download dashboard">
            <button onClick={() => downloadDashboardExport("csv")} disabled={!!exportingDashboard} className="rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50">{exportingDashboard === "csv" ? "Preparing…" : "↓ CSV"}</button>
            <button onClick={() => downloadDashboardExport("xlsx")} disabled={!!exportingDashboard} className="rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50">{exportingDashboard === "xlsx" ? "Preparing…" : "↓ Excel"}</button>
            <button onClick={() => downloadDashboardExport("pdf")} disabled={!!exportingDashboard} className="rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50">{exportingDashboard === "pdf" ? "Preparing…" : "↓ PDF"}</button>
          </div>
          <AnimatePresence mode="wait">
            {lastUpdated && (
              <motion.span
                key="updated"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="text-xs text-muted-foreground hidden sm:block"
              >
                Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </motion.span>
            )}
          </AnimatePresence>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors ${
              autoRefresh
                ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400"
                : "bg-muted text-muted-foreground"
            }`}
          >
            <motion.span
              animate={autoRefresh ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 1.5, repeat: Infinity }}
              className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${autoRefresh ? "bg-green-500" : "bg-gray-400"}`}
            />
            <span className="hidden xs:inline">{autoRefresh ? "Live" : "Paused"}</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300 }}
            onClick={() => fetchData(false)}
            className="p-1.5 md:p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
            title="Refresh now"
          >
            <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </motion.button>
        </div>
      </motion.div>

      {/* Row 2: KPI Revenue Goal Card */}
      <KPIBar isAdmin={(session?.user as { role?: string } | undefined)?.role === "admin"} currentCash={metrics?.totalCashCollected ?? 0} />

      {/* Row 3: Filters - all in one uniform line */}
      <FilterBar
        dateLabel={formatForDisplay()}
        showDateFieldToggle={true}
        dateField={dateField as "timestamp" | "dateCallWasTaken"}
        onDateFieldChange={(v) => setDateField(v)}
        filters={[
          {
            key: "bookingChannel",
            label: "Call Booking Type",
            options: [
              { value: "inbound", label: "Inbound (Self-Booked)" },
              { value: "outbound", label: "Outbound (Setter Booked)" },
            ],
            value: bookingChannel,
            onChange: (value) => setBookingChannel(value as "" | "inbound" | "outbound"),
          },
          {
            key: "funnel",
            label: "All Funnels",
            options: filterOptions.funnels.map((f) => ({ value: f.value, label: f.label })),
            value: funnels,
            onChange: (v) => setFunnels(v as string[]),
            multiple: true,
          },
          {
            key: "funnelType",
            label: "Funnel Type",
            options: [
              { value: "webinar", label: "Webinar" },
              { value: "bookCall", label: "Book a Call" },
              { value: "application", label: "Application" },
              { value: "leadMagnet", label: "Lead Magnet" },
            ],
            value: funnelType,
            onChange: (value) => setFunnelType(value as "" | "webinar" | "bookCall" | "application" | "leadMagnet"),
          },
          {
            key: "leadType",
            label: "All Lead Types",
            options: [
              { value: "applicant", label: "Applicant" },
              { value: "optin", label: "Opt-in" },
            ],
            value: leadTypes,
            onChange: (v) => setLeadTypes(v as string[]),
            multiple: true,
          },
          {
            key: "content",
            label: "Select Content",
            options: filterOptions.contents.map((c) => ({ value: c.value, label: c.label.length > 30 ? c.label.slice(0, 30) + "..." : c.label })),
            value: contents,
            onChange: (v) => setContents(v as string[]),
            multiple: true,
          },
          {
            key: "closer",
            label: "Select Closers",
            options: filterOptions.closers.map((c) => ({ value: c, label: c })),
            value: closers,
            onChange: (v) => setClosers(v as string[]),
            multiple: true,
          },
          {
            key: "setter",
            label: "Select Setters",
            options: filterOptions.setters.map((s) => ({ value: s, label: s })),
            value: setters,
            onChange: (v) => setSetters(v as string[]),
            multiple: true,
          },
        ]}
        onClearAll={handleClearAll}
      />

      {/* Main Metric Cards */}
      <div className="grid gap-6 md:grid-cols-3">
        <MetricCard
          label="Cash Collected"
          value={formatCurrencyAbbreviated(metrics?.totalCashCollected || 0)}
          icon={<DollarIcon />}
          color="green"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={0}
        />
        <MetricCard
          label="Revenue"
          value={formatCurrencyAbbreviated(metrics?.totalRevenue || 0)}
          icon={<TrendingUpIcon />}
          color="brand"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={1}
        />
        <MetricCard
          label="Close Rate"
          value={formatPercent(metrics?.closeRate || 0)}
          icon={<TargetIcon />}
          color="blue"
          data={[]}
          tooltipFormatter={(v) => formatPercent(v)}
          index={2}
        />
      </div>

      {/* Call Statistics */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-6">
        <StatCard label="Show Rate" value={formatPercent(metrics?.showRate || 0)} index={0} />
        <StatCard label="Calls Due (Scheduler)" value={(metrics?.schedulerCallsDue || 0).toLocaleString()} index={1} />
        <StatCard label="Calls Cancelled" value={(metrics?.cancelledCalls || 0).toLocaleString()} index={2} />
        <StatCard label="Calls Due (EOCs)" value={(metrics?.callsDue || 0).toLocaleString()} index={3} />
        <StatCard label="Calls Taken" value={(metrics?.callsTaken || 0).toLocaleString()} index={4} />
        <StatCard label="Calls Closed" value={(metrics?.callsClosed || 0).toLocaleString()} index={5} />
      </div>

      {/* Average Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Avg. Cash/Call" value={formatCurrency(metrics?.avgCashPerCall || 0)} index={4} />
        <StatCard label="Avg. Cash/Close" value={formatCurrency(metrics?.avgCashPerClose || 0)} index={5} />
        <StatCard label="Avg. Cash/Application" value={formatCurrency(metrics?.avgCashPerApplication || 0)} index={6} />
        <StatCard label="Avg. Cash/Opt-in" value={formatCurrency(metrics?.avgCashPerOptin || 0)} index={7} />
      </div>

      {/* Cash Totals */}
      <div className="grid gap-4 grid-cols-3">
        <StatCard label="Total Cash (Applications)" value={formatCurrency(metrics?.totalApplicationsCash || 0)} index={8} />
        <StatCard label="Total Cash (Opt-ins)" value={formatCurrency(metrics?.totalOptinsCash || 0)} index={9} />
        <StatCard label="Total Cash (DM Bookings)" value={formatCurrency(metrics?.totalDMBookingsCash || 0)} index={10} />
      </div>

      {/* Additional Statistics */}
      <div className="grid gap-4 md:grid-cols-6">
        {[
          { label: "New Cash Collected", value: formatCurrency(metrics?.newCashCollected || 0), description: "Total cash from new deals (excludes MRR)" },
          { label: "MRR", value: formatCurrency(metrics?.mrrCash || 0), description: "Monthly recurring revenue" },
          { label: "Total Gross Revenue", value: formatCurrency(metrics?.totalGrossRevenue || 0), description: "Total contract value (all revenue)" },
          { label: "Total Applications", value: (metrics?.totalApplications || 0).toLocaleString() },
          { label: "Total Opt-ins", value: (metrics?.totalOptins || 0).toLocaleString() },
          { label: "Total DM Bookings", value: (metrics?.totalDMBookings || 0).toLocaleString(), description: "DMS chat with Marsh + DMS main page + CWM DMs" },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 15, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 0.4,
              delay: 0.5 + i * 0.06,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            whileHover={{
              y: -3,
              boxShadow: "0 12px 40px -10px rgba(0, 0, 0, 0.2)",
              transition: { duration: 0.2 },
            }}
            onClick={stat.onClick}
            className={["bg-card rounded-xl border-2 border-border p-4 md:p-6 transition-colors duration-300 hover:border-border/80", stat.onClick ? "cursor-pointer hover:border-[var(--brand-primary)]" : ""].join(" ")}
          >
            <motion.p
              className="text-xs md:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 md:mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 + i * 0.06 }}
            >
              {stat.label}
            </motion.p>
            <p className="text-xl md:text-3xl font-bold">{stat.value}</p>
            {stat.description && (
              <motion.p
                className="text-xs text-muted-foreground mt-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 + i * 0.06 }}
              >
                {stat.description}
              </motion.p>
            )}
          </motion.div>
        ))}
      </div>

      {/* Closes by Country Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border p-6 overflow-hidden"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">
              {chartFilterMode === "closed" ? "Closes" : "Leads"} by Country
            </h3>
            <AnimatePresence>
              {country && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  onClick={() => setCountry("")}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                >
                  <span>Clear: {country}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>
          </div>
          <motion.select
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            value={chartFilterMode}
            onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
          >
            <option value="closed">Closed Leads</option>
            <option value="all">All Leads</option>
          </motion.select>
        </div>
        <div className="h-[400px] cursor-pointer">
          <ResponsivePie
            data={toNivoPieData(countryData, chartColors)}
            margin={{ top: 40, right: 120, bottom: 40, left: 120 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            sortByValue={false}
            colors={{ datum: "data.color" }}
            borderWidth={2}
            borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
            enableArcLinkLabels={true}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabel={(d) => String(d.label)}
            arcLinkLabelsStraightLength={20}
            arcLinkLabelsDiagonalLength={20}
            enableArcLabels={true}
            arcLabelsSkipAngle={10}
            arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
            arcLabelsTextColor="#ffffff"
            onClick={(node) => handleCountryClick(String(node.label))}
            theme={getNivoPieTheme()}
            tooltip={PieChartTooltip}
          />
        </div>
      </motion.div>

      {/* Cash Collected Trend and Lead Source */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Cash Collected Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <h3 className="text-lg font-semibold mb-4">
            Cash Collected Trend
            {trendType === "daily" && (
              <span className="text-xs font-normal text-muted-foreground ml-2">(Daily)</span>
            )}
          </h3>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cashTrend} margin={{ top: 10, right: 5, left: -10, bottom: 10 }}>
                <defs>
                  <pattern
                    id="gridPattern"
                    x="0"
                    y="0"
                    width="20"
                    height="40"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M 20 0 L 0 0 0 20"
                      fill="none"
                      stroke="hsl(var(--border))"
                      strokeWidth="0.5"
                      strokeOpacity="0.5"
                    />
                  </pattern>
                  <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={areaStroke} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={areaStroke} stopOpacity={0.1} />
                  </linearGradient>
                  <filter id="dotShadow" x="-100%" y="-100%" width="300%" height="300%">
                    <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.4)" />
                  </filter>
                </defs>
                <rect
                  x="32"
                  y="0"
                  width="100%"
                  height="100%"
                  fill="url(#gridPattern)"
                  style={{ pointerEvents: "none" }}
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: "#a1a1aa" }}
                  tickMargin={6}
                  interval="preserveStartEnd"
                  tickFormatter={(val) => {
                    if (trendType === "monthly") {
                      const [year, month] = val.split("-");
                      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                      const monthName = monthNames[parseInt(month, 10) - 1] || month;
                      return `${monthName} ${year}`;
                    }
                    const [, month, day] = val.split("-");
                    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                    const monthName = monthNames[parseInt(month, 10) - 1] || month;
                    return `${monthName} ${parseInt(day, 10)}`;
                  }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: "#a1a1aa" }}
                  tickFormatter={(val) => {
                    if (val === 0) return "$0";
                    if (val >= 1000000) return `$${(val / 1000000).toFixed(0)}M`;
                    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
                    return `$${val}`;
                  }}
                  tickMargin={2}
                  width={32}
                />
                <Tooltip
                  cursor={{ stroke: areaStroke, strokeWidth: 1 }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      let displayLabel = label;
                      if (trendType === "monthly" && typeof label === "string") {
                        const [year, month] = label.split("-");
                        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const monthName = monthNames[parseInt(month || "", 10) - 1] || month;
                        displayLabel = `${monthName} ${year}`;
                      } else if (typeof label === "string") {
                        const [year, month, day] = label.split("-");
                        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const monthName = monthNames[parseInt(month || "", 10) - 1] || month;
                        displayLabel = `${monthName} ${parseInt(day || "", 10)}, ${year}`;
                      }
                      return (
                        <div className="rounded-lg bg-card border border-border p-3 shadow-lg">
                          <div className="text-xs font-medium mb-1 text-muted-foreground">{displayLabel}</div>
                          <div className="text-xs font-medium mb-1 text-muted-foreground">Cash:</div>
                          <div className="text-sm font-semibold text-foreground">{formatCurrency(payload[0]?.value as number)}</div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={areaStroke}
                  strokeWidth={2}
                  fill="url(#areaGradient)"
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: areaStroke,
                    stroke: "white",
                    strokeWidth: 2,
                    filter: "url(#dotShadow)",
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>


      <AnimatePresence>
        {cancelledCallsModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setCancelledCallsModal(null)}>
            <motion.div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-6xl max-h-[85vh] flex flex-col" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} onClick={event => event.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 border-b border-border p-5">
                <div><h2 className="text-lg font-semibold">Cancelled Calls</h2><p className="text-xs text-muted-foreground mt-1">Included by {cancelledCallsModal.datePolicy}. {cancelledCallsModal.metrics.confirmed} confirmed · {cancelledCallsModal.metrics.unknownClassification} needs review.</p></div>
                <button aria-label="Close cancelled calls" onClick={() => setCancelledCallsModal(null)} className="rounded p-2 text-muted-foreground hover:bg-muted hover:text-foreground">✕</button>
              </div>
              {!cancelledCallsModal.slackAvailable && <div className="mx-5 mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">Slack reason source unavailable: {cancelledCallsModal.slackError || "not configured"}. Source reason is shown only when provided by iClosed/Zap.</div>}
              <div className="overflow-auto p-5">
                <table className="w-full min-w-[1080px] text-left text-sm"><thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="px-3 py-2">Lead</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Cancelled at (ET)</th><th className="px-3 py-2">Scheduled call (ET)</th><th className="px-3 py-2">Reason</th><th className="px-3 py-2">Source</th></tr></thead><tbody className="divide-y divide-border">
                  {cancelledCallsModal.rows.map(row => <tr key={row.callId} className="align-top"><td className="px-3 py-3"><p className="font-medium">{row.name}</p><p className="text-xs text-muted-foreground">{row.email || "No email recorded"}</p></td><td className="px-3 py-3 capitalize">{row.classification === "unknown" ? "Needs review" : row.classification}</td><td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{formatEasternDateTime(row.cancelledAt)}</td><td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{formatEasternDateTime(row.scheduledCallAt)}</td><td className="px-3 py-3 whitespace-pre-wrap">{row.cancellationReason}<p className="mt-1 text-xs text-muted-foreground">{row.reasonSource === "setter_thread" ? "Human Slack thread replies" : row.reasonSource === "source_reason" ? "iClosed/Zap source reason" : "Not recorded"}</p></td><td className="px-3 py-3">{row.slackThreadUrl ? <a className="text-primary underline" href={row.slackThreadUrl} target="_blank" rel="noreferrer">Slack thread</a> : <span className="text-muted-foreground">Unavailable</span>}</td></tr>)}
                  {!cancelledCallsModal.rows.length && <tr><td className="px-3 py-10 text-center text-muted-foreground" colSpan={6}>No cancellation-feed rows match these filters.</td></tr>}
                </tbody></table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Closes by Lead Source */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold">
                {chartFilterMode === "closed" ? "Closes" : "Leads"} By Lead Source
              </h3>
              <AnimatePresence>
                {source && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={() => setSource("")}
                    className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                  >
                    <span>Clear: {source}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
            <motion.select
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              value={chartFilterMode}
              onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
              className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
            >
              <option value="closed">Closed Leads</option>
              <option value="all">All Leads</option>
            </motion.select>
          </div>
          <div className="h-[300px] cursor-pointer">
            <ResponsivePie
              data={toNivoPieData(leadSourceData, chartColors)}
              margin={{ top: 30, right: 100, bottom: 30, left: 100 }}
              innerRadius={0.5}
              padAngle={1}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={2}
              borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
              enableArcLinkLabels={true}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => String(d.label)}
              arcLinkLabelsStraightLength={15}
              arcLinkLabelsDiagonalLength={15}
              enableArcLabels={true}
              arcLabelsSkipAngle={10}
              arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
              arcLabelsTextColor="#ffffff"
              onClick={(node) => handleSourceClick(String(node.label))}
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
            />
          </div>
        </motion.div>
      </div>

      {/* Closes by Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border p-6 overflow-hidden"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">
              {chartFilterMode === "closed" ? "Closes" : "Leads"} by Content
            </h3>
            <AnimatePresence>
              {contents.length > 0 && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  onClick={() => setContents([])}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                >
                  <span>Clear: {contents.length > 1 ? `${contents.length} selected` : (contents[0].length > 20 ? contents[0].slice(0, 20) + "..." : contents[0])}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>
          </div>
          <motion.select
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            value={chartFilterMode}
            onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
          >
            <option value="closed">Closed Leads</option>
            <option value="all">All Leads</option>
          </motion.select>
        </div>
        <div className="h-[500px] cursor-pointer">
          <ResponsivePie
            data={toNivoPieData(contentData, chartColors)}
            margin={{ top: 40, right: 140, bottom: 40, left: 140 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            sortByValue={false}
            colors={{ datum: "data.color" }}
            borderWidth={2}
            borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
            enableArcLinkLabels={true}
            arcLinkLabelsSkipAngle={8}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabel={(d) => {
              const label = String(d.label);
              return label.length > 25 ? label.slice(0, 22) + "..." : label;
            }}
            arcLinkLabelsStraightLength={20}
            arcLinkLabelsDiagonalLength={20}
            enableArcLabels={true}
            arcLabelsSkipAngle={10}
            arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
            arcLabelsTextColor="#ffffff"
            onClick={(node) => handleContentClick(String(node.label))}
            theme={getNivoPieTheme()}
            tooltip={PieChartTooltip}
          />
        </div>
      </motion.div>

      {/* Recent Calls Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Recent Calls</h3>
          <p className="text-sm text-muted-foreground">Latest call activity</p>
        </div>
        <DataTable
          data={recentCalls}
          columns={recentCallsColumns}
          rowKey="id"
          emptyMessage="No recent calls found."
          loading={tableLoading}
          serverPagination={{
            currentPage: tablePage,
            pageSize: tablePageSize,
            totalItems: tableTotalItems,
            totalPages: tableTotalPages,
            pageSizeOptions: [10, 25, 50, 100],
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
          onEdit={handleEditCall}
          onDelete={handleDeleteCall}
          onRowClick={(call) => call.email && setSelectedLeadEmail(call.email)}
        />
      </motion.div>

      {/* Edit Dialog */}
      <EditRowDialog
        open={!!editingCall}
        onOpenChange={(open) => !open && setEditingCall(null)}
        title="Edit Call"
        description="Update call information"
        fields={CALL_EDIT_FIELDS}
        data={editingCall as Record<string, unknown> | null}
        onSave={handleSaveCall}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={!!deletingCall}
        onOpenChange={(open) => !open && setDeletingCall(null)}
        title="Delete Call"
        itemName={deletingCall?.leadName}
        onConfirm={confirmDeleteCall}
      />

      {/* Lead Timeline Drawer */}
      <AnimatePresence>
        {selectedLeadEmail && (
          <LeadTimelineDrawer
            email={selectedLeadEmail}
            onClose={() => setSelectedLeadEmail(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
